import { programPassengerKey } from './programPlanning.js';
import { preserveReview, vanNumber } from './planningWorkspace.js';

export const passengerRegion = person => [person.cidade || 'Cidade não informada', person.regiao || 'Sem região'].join(' · ');

export const distributionPeople = groups => groups.flatMap((group, index) => group.pessoas.map(person => ({
  person, key: programPassengerKey(person), groupIndex: index, van: vanNumber(group, index),
})));

// Stage the whole arrangement before enforcing capacity. A full van can exchange
// passengers with another full van without requiring a temporary third van.
export function previewRegionalDistribution(groups, assignments = {}, extraVans = 0, firstNewVan = 1) {
  const rows = distributionPeople(groups);
  const known = new Set(rows.map(row => row.key));
  if (known.size !== rows.length) throw new Error('Há passageiros repetidos. Atualize o planejamento antes de distribuir.');
  if (Object.keys(assignments).some(key => !known.has(key))) throw new Error('A lista de passageiros mudou. Descarte a prévia e confira a distribuição atual.');
  if (!Number.isInteger(extraVans) || extraVans < 0 || extraVans > rows.length) throw new Error('Quantidade de vans inválida.');
  const nextNumber = Math.max(firstNewVan, Math.max(0, ...groups.map(vanNumber)) + 1);
  const slots = [...groups.map(group => ({ ...group, pessoas: [] })), ...Array.from({ length: extraVans }, (_, index) => ({
    planejamento_van_numero: nextNumber + index, pessoas: [], motorista: null, veiculo: null, conferida: false,
  }))];
  let moved = 0;
  for (const row of rows) {
    const target = Object.hasOwn(assignments, row.key) ? Number(assignments[row.key]) : row.groupIndex;
    if (!Number.isInteger(target) || !slots[target]) throw new Error('Uma van de destino não está disponível. Confira a distribuição.');
    if (target !== row.groupIndex) moved++;
    const slot = slots[target];
    if (target >= groups.length && !slot.pessoas.length) {
      const source = groups[row.groupIndex];
      for (const field of ['programacao_responsavel_id', 'programacao_codigo_responsavel', 'data', 'horario', 'trecho']) slot[field] = source[field];
    }
    slot.pessoas.push(row.person);
  }
  const errors = [], removed = [];
  const result = slots.map((slot, index) => {
    const capacity = slot.veiculo?.id ? Number(slot.veiculo.capacidade) || 15 : null;
    if (capacity && slot.pessoas.length > capacity) errors.push(`Van ${vanNumber(slot,index)}: ${slot.pessoas.length} passageiros para ${capacity} lugares. Destine parte deles a outra van.`);
    if (!slot.pessoas.length && index < groups.length) removed.push(vanNumber(slot,index));
    return groups[index] ? preserveReview(groups[index], slot) : slot;
  });
  return { slots: result, groups: result.filter(slot => slot.pessoas.length), moved, errors, removed,
    reviewed: result.some((slot,index) => groups[index]?.conferida && !slot.conferida) };
}

// Pure preview: moving never edits a demand, creates a passenger or mutates its origin.
export function previewPassengerTransfer(groups, selectedKeys, destination) {
  const selected = new Set(selectedKeys);
  const rows = distributionPeople(groups);
  const keys = rows.map(row => row.key);
  if (new Set(keys).size !== keys.length) throw new Error('Há passageiros repetidos na distribuição. Atualize o planejamento antes de transferir.');
  if ([...selected].some(key => !keys.includes(key))) throw new Error('A lista de passageiros mudou. Selecione novamente quem deseja mover.');
  const isNew = destination === 'new';
  const targetIndex = isNew ? groups.length : Number(destination);
  if (destination === '' || !isNew && (!Number.isInteger(targetIndex) || !groups[targetIndex])) throw new Error('Escolha a van de destino.');
  const moving = rows.filter(row => selected.has(row.key) && row.groupIndex !== targetIndex);
  if (!moving.length) throw new Error('Selecione passageiros que estejam em outra van.');
  const target = isNew ? null : groups[targetIndex];
  const targetCount = (target?.pessoas.length || 0) + moving.length;
  const capacity = target?.veiculo?.id ? Number(target.veiculo.capacidade) || 15 : null;
  if (capacity && targetCount > capacity) throw new Error(`A van ${vanNumber(target, targetIndex)} ficaria com ${targetCount} passageiros para ${capacity} lugares. Mova menos pessoas ou escolha um veículo maior.`);
  const movingKeys = new Set(moving.map(row => row.key));
  const removed = [];
  const affected = [];
  const next = groups.flatMap((group, index) => {
    const people = index === targetIndex ? [...group.pessoas, ...moving.map(row => row.person)]
      : group.pessoas.filter(person => !movingKeys.has(programPassengerKey(person)));
    if (people.length !== group.pessoas.length) affected.push({ number: vanNumber(group, index), before: group.pessoas.length, after: people.length });
    if (!people.length) { removed.push(vanNumber(group, index)); return []; }
    return [preserveReview(group, { ...group, pessoas: people })];
  });
  if (isNew) {
    const source = groups[moving[0].groupIndex];
    next.push({ regiao: '', pessoas: moving.map(row => row.person), motorista: null, veiculo: null,
      programacao_responsavel_id: source.programacao_responsavel_id,
      programacao_codigo_responsavel: source.programacao_codigo_responsavel,
      data: source.data, horario: source.horario, trecho: source.trecho, conferida: false });
  }
  return { groups: next, moved: moving.length, removed, affected, targetCount, capacity, isNew,
    reviewed: groups.some((group,index) => group.conferida && affected.some(item => item.number === vanNumber(group,index))) };
}
